import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { ViewNames } from '../../../../navigation/reverseUrl'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useModal } from '../../../../hooks/useModal'
import { CreateUpdateFolderModal } from '../CreateUpdateFolderModal'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { CreateUpdateDocumentModal } from '../CreateUpdateDocumentModal'
import { ItemType } from '../item-type'
import { DocumentsListItemDraggable } from './DocumentListItemDraggable'
import { FolderDropWrapper } from './FolderDropWrapper'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { deleteDocument } from '../../../../api/documents'

interface DocumentsListItemProps {
  document: IDocumentModel
  documentRoute: ViewNames
  isListMode: boolean
}

export const DocumentsListItem = ({
  document,
  documentRoute,
  isListMode,
}: DocumentsListItemProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const getType = (): ItemType => {
    switch (document.type) {
      case 'folder':
        return ItemType.FOLDER
      default:
        return ItemType.DOCUMENT
    }
  }

  const documentType = getType()

  const isFolder = documentType === ItemType.FOLDER
  const { mutate: mutateDeleteDocument } = useMutation({
    mutationFn: async (params: { documentId: string }) => {
      await deleteDocument(params.documentId)
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          isFolder
            ? 'createUpdateFolder.flashMessage.deleteFolderSuccess'
            : 'createUpdateDocument.flashMessage.deleteDocumentSuccess'
        )
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate(
          isFolder
            ? 'createUpdateFolder.flashMessage.deleteFolderFailure'
            : 'createUpdateDocument.flashMessage.deleteDocumentFailure'
        )
      )
    },
  })
  const {
    show: showEditFolderModal,
    open: openEditFolderModal,
    close: closeEditFolderModal,
  } = useModal()
  const {
    show: showEditDocumentModal,
    open: openEditDocumentModal,
    close: closeEditDocumentModal,
  } = useModal()
  const {
    show: showRemoveConfirmationModal,
    open: openRemoveConfirmationModal,
    close: closeRemoveConfirmationModal,
  } = useModal()

  const removeFolder = (): void => {
    mutateDeleteDocument({ documentId: document._id })
  }

  return (
    <>
      {documentType === ItemType.DOCUMENT ? (
        <DocumentsListItemDraggable
          document={document}
          documentRoute={documentRoute}
          openEditDocumentModal={openEditDocumentModal}
          openEditFolderModal={openEditFolderModal}
          openRemoveConfirmationModal={openRemoveConfirmationModal}
          isListMode={isListMode}
        />
      ) : (
        <FolderDropWrapper
          document={document}
          documentRoute={documentRoute}
          openEditDocumentModal={openEditDocumentModal}
          openEditFolderModal={openEditFolderModal}
          openRemoveConfirmationModal={openRemoveConfirmationModal}
          isListMode={isListMode}
        />
      )}
      {document.parentId && document.section && (
        <CreateUpdateFolderModal
          show={showEditFolderModal}
          close={closeEditFolderModal}
          folder={document}
          parentId={document.parentId}
          section={document.section}
        />
      )}
      {document.parentId && document.section && (
        <CreateUpdateDocumentModal
          show={showEditDocumentModal}
          close={closeEditDocumentModal}
          parentId={document.parentId}
          section={document.section}
          document={document}
        />
      )}
      <ConfirmationModal
        title={translate(
          isFolder
            ? 'createUpdateFolder.removeFolderConfirmationModal.title'
            : 'createUpdateDocument.removeDocumentConfirmationModal.title'
        )}
        description={translate(
          isFolder
            ? 'createUpdateFolder.removeFolderConfirmationModal.description'
            : 'createUpdateDocument.removeDocumentConfirmationModal.description'
        )}
        show={showRemoveConfirmationModal}
        close={closeRemoveConfirmationModal}
        onConfirm={removeFolder}
        deleteMode
      />
    </>
  )
}
